import {
  toWSDate,
  WSIcon,
  WSList,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableProps
} from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import { Engagement } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";

import { useQueryRequirementDefinitionsSetting } from "../../../../query/requirementDefinitions/query/useQueryRequirementDefinitionsSetting";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { selectorPolicyTypesFromSettings } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { mapRequirementDefinitionTemplateTierConfig } from "../../config/maps";
import { useRequirementDefinitionDefaultActions } from "../../hooks/useRequirementDefinitionDefaultActions";
import {
  buildRequirementDetailsPath,
  useGoToRequirementDefinitionDetails
} from "../../paths/helpers";
import {
  getRequirementDefinitionTableCellProps,
  getRequirementDefinitionTier
} from "../../utils";
import { useVersionQuickReferenceModal } from "../VersionQuickReferenceModal";

export interface RequirementsTableProps extends Omit<
  WSTableProps,
  "tableData"
> {
  listPath: string;
  requirements: IRequirementDefinitionResponse[];
  engagements: Engagement[];
}

export const TypeCell: React.FC<{
  requirementType: RequirementType;
  requirementDataSourceSettingsId?: string;
}> = ({ requirementType, requirementDataSourceSettingsId }) => {
  const querySettings = useQueryRequirementDefinitionsSetting({
    fetch: "contextType",
    requirementDefinition: {
      dataSourceId: requirementDataSourceSettingsId!,
      requirementType
    }
  });

  const dataSourceSettings = querySettings.data;

  const props = getRequirementDefinitionTableCellProps({
    contextType:
      dataSourceSettings && "contextType" in dataSourceSettings
        ? dataSourceSettings.contextType
        : undefined,
    policyType:
      dataSourceSettings && "coverages" in dataSourceSettings
        ? selectorPolicyTypesFromSettings(dataSourceSettings)
        : undefined,
    type: requirementType
  });

  if (querySettings.isLoading) {
    return <WSTableCell icon="loader" secondaryText="..." text="..." />;
  }

  return <WSTableCell {...props} />;
};

export const TierCell: React.FC<{
  requirementType: RequirementType;
  requirementDataSourceSettingsId?: string;
}> = ({ requirementType, requirementDataSourceSettingsId }) => {
  const querySettings = useQueryRequirementDefinitionsSetting({
    fetch: "contextType",
    requirementDefinition: {
      dataSourceId: requirementDataSourceSettingsId!,
      requirementType
    }
  });

  const dataSourceSettings = querySettings.data;

  const tier = getRequirementDefinitionTier({
    contextType:
      dataSourceSettings && "contextType" in dataSourceSettings
        ? dataSourceSettings.contextType
        : undefined,
    policyType:
      dataSourceSettings && "coverages" in dataSourceSettings
        ? selectorPolicyTypesFromSettings(dataSourceSettings)
        : undefined,
    type: requirementType
  });
  const tierConfig = mapRequirementDefinitionTemplateTierConfig[tier];

  if (querySettings.isLoading) {
    return <WSTableCell icon="loader" secondaryText="..." text="..." />;
  }

  return (
    <WSTableCell
      icon={tierConfig.isWayfinder ? "compass" : undefined}
      secondaryText={tierConfig.description}
      text={tierConfig.name}
    />
  );
};

export const RequirementDefinitionsTable: React.FC<RequirementsTableProps> = ({
  listPath,
  requirements,
  engagements,
  loading,
  ...elementProps
}) => {
  const requirementDefinitionDefaultActions =
    useRequirementDefinitionDefaultActions();

  const versionQuickReferenceModal = useVersionQuickReferenceModal();

  const goToRequirementDefinitionDetails =
    useGoToRequirementDefinitionDetails();

  const tableData = useMemo(
    () =>
      requirements.map(requirement => ({
        data: requirement,
        id: requirement.requirementDefinitionId
      })),
    [requirements]
  );

  const columns: Array<WSTableColumn<IRequirementDefinitionResponse>> = [
    {
      config: {
        header: {
          text: "Name"
        }
      },
      renderFunction: rowItem => (
        <WSTableCell
          secondaryText={rowItem.data.description}
          text={rowItem.data.name}
        />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: ({ data: requirementDefinition }) => (
        <TypeCell
          requirementDataSourceSettingsId={requirementDefinition.dataSourceId}
          requirementType={requirementDefinition.requirementType}
        />
      )
    },
    {
      config: {
        header: {
          text: "Tier"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: ({ data: requirementDefinition }) => (
        <TierCell
          requirementDataSourceSettingsId={requirementDefinition.dataSourceId}
          requirementType={requirementDefinition.requirementType}
        />
      )
    },
    {
      config: {
        header: {
          text: "Engagements"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => {
        const requirementEngagements = engagements.filter(e =>
          e.requirementIds?.includes(rowItem.id)
        );
        const firstTwoEngagementsNames = requirementEngagements
          .slice(0, 2)
          .map(e => e.name)
          .join(", ");
        return (
          <WSTableCell
            text={firstTwoEngagementsNames}
            {...(requirementEngagements.length > 2
              ? {
                  secondaryText: `+${requirementEngagements.length - 2} more`
                }
              : {})}
          />
        );
      }
    },
    /*
    {
      config: {
        header: {
          text: "Approvals"
        },
        hideOnScreens: ["XS", "S"]
      },
      renderFunction: rowItem => (
        <WSTableCell
          text={
            rowItem.data.reviewStrategy ===
            RequirementReviewStrategy.AlwaysRequired
              ? "Always"
              : rowItem.data.reviewStrategy === RequirementReviewStrategy.OnFail
              ? "On fail"
              : "Never"
          }
        />
      )
    },
    {
      config: {
        header: {
          text: "Expiration"
        },
        hideOnScreens: ["XS", "S"]
      },
      renderFunction: rowItem => (
        <WSTableCell
          text={
            rowItem.data.expirationStrategy ===
            RequirementExpirationStrategy.DaysAfterComplete
              ? `${rowItem.data.expirationDays} days after complete`
              : "Never"
          }
        />
      )
    },
    */
    {
      config: {
        header: {
          text: "Last updated"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => (
        <WSList.Horizontal
          alignItems="center"
          fullWidth
          justify="space-between"
        >
          <WSTableCell
            secondaryText={
              toWSDate(rowItem.data.updatedAt, "monthDayYear").formattedValue
            }
            text={`v${rowItem.data.version}`}
          />
          <WSIcon
            color="gray500"
            invisible={!rowItem.data.versionHistory}
            name="info-circle-fill"
            onClick={() =>
              versionQuickReferenceModal.open({
                onViewDetails: () =>
                  goToRequirementDefinitionDetails(
                    rowItem.data.requirementDefinitionId
                  ),
                requirementDefinitionId: rowItem.data.requirementDefinitionId
              })
            }
          />
        </WSList.Horizontal>
      )
    },
    {
      config: {
        header: {
          text: "Status"
        }
      },
      renderFunction: ({ data: requirement }) => {
        if (requirement.status === RequirementDefinitionStatus.Active) {
          return (
            <WSTableCell
              pill={{
                icon: true,
                text: "Active",
                theme: "success"
              }}
            />
          );
        }
        return (
          <WSTableCell
            pill={{
              icon: "archive",
              text: "Archived",
              theme: "neutral"
            }}
          />
        );
      }
    }
  ];

  return (
    <WSTable<IRequirementDefinitionResponse>
      {...elementProps}
      columns={columns}
      data-testid="requirementsTable"
      loading={loading}
      rowFetchMenuActions={({ data }) => {
        return requirementDefinitionDefaultActions.getMenuActions({
          engagements: engagements,
          requirementDefinition: data
        });
      }}
      tableData={tableData}
      onRowClick={({ data: requirementDefinition }) => {
        goToRequirementDefinitionDetails(
          requirementDefinition.requirementDefinitionId
        );
      }}
      onRowControlClick={({ data: requirementDefinition }) => {
        openInNewTab(
          buildRequirementDetailsPath(
            requirementDefinition.requirementDefinitionId
          )
        );
      }}
      onRowMousewheelClick={({ data: requirementDefinition }) => {
        openInNewTab(
          buildRequirementDetailsPath(
            requirementDefinition.requirementDefinitionId
          )
        );
      }}
    />
  );
};
